import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Spinner,
  Textarea,
} from '@fluentui/react-components';
import { useMutation } from '@tanstack/react-query';
import { Copy, KeyRound } from 'lucide-react';

import { RecoveryRevealResult, revealRecoveryPassword } from '../api/devices';
import { ApiError } from '../api/client';
import { STROKE, SURFACE, TEXT } from '../theme/tokens';

import EnvironmentEcho from './EnvironmentEcho';
// FD-313 (FD-ADR-033) — the audited recovery-password reveal, shaped like the
// FD-ADR-027 override (the closest existing pattern): an explicit action, a
// REQUIRED reason, a confirm step, and the value revealed in place with a copy
// affordance. The value exists in the DOM only after the explicit reveal — it
// is never pre-fetched, never in a grid, a tooltip, or a URL.
//
// The dialog stays MOUNTED and toggles `open` (the Fluent v9 a11y-tree gotcha:
// a Dialog mounted already-open never enters the accessibility tree).
//
// `serialNumber` fixed: launched from a salvaged device's detail page.
// `serialNumber` absent: launched from Resources › Devices for a device that no
// longer belongs to ANY campaign — the whole point of the salvage registry is
// that removal must not orphan the credential, so the operator types the serial.

export interface RecoveryRevealDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serialNumber?: string;
}

export default function RecoveryRevealDialog({
  open,
  onOpenChange,
  serialNumber,
}: RecoveryRevealDialogProps) {
  const [serial, setSerial] = useState(serialNumber ?? '');
  const [reason, setReason] = useState('');
  const [copied, setCopied] = useState(false);

  const effectiveSerial = (serialNumber ?? serial).trim();

  const reveal = useMutation<RecoveryRevealResult, unknown>({
    mutationFn: () => revealRecoveryPassword(effectiveSerial, reason.trim()),
  });

  const close = () => {
    // The value must not survive the dialog: closing clears the mutation state,
    // so re-opening starts from the confirm step, never from a lingering secret.
    reveal.reset();
    setReason('');
    setCopied(false);
    if (!serialNumber) setSerial('');
    onOpenChange(false);
  };

  const errorMessage = (() => {
    if (!reveal.isError) return null;
    const e = reveal.error;
    if (e instanceof ApiError && e.status === 404) {
      // Deliberately as uninformative as the server: no oracle.
      return 'No retrievable recovery password for this device. Either it was never salvaged, or it already completed a later migration (its old credential is gone by design).';
    }
    return e instanceof Error ? e.message : 'The reveal failed unexpectedly.';
  })();

  return (
    <Dialog open={open} onOpenChange={(_, data) => (data.open ? onOpenChange(true) : close())}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Reveal recovery password</DialogTitle>
          <DialogContent>
            <div className="flex flex-col gap-3">
              {/* FD-422 — the reveal reads a credential out of THIS deployment's
                  Key Vault and is audited against this operator. */}
              <EnvironmentEcho />

              <p className="font-display text-[13px]" style={{ color: TEXT.secondary }}>
                This is the local administrator password of a salvaged device — a machine that
                left its source tenant and could not join the destination. It is the only
                working credential on that device, and it stays valid until the device
                successfully completes a later migration. The reveal is audited: who, when,
                and the reason below are recorded on the device&rsquo;s event trail.
              </p>

              {!serialNumber && (
                <Field label="Serial number" required>
                  <Input
                    value={serial}
                    onChange={(_, d) => setSerial(d.value)}
                    placeholder="The salvaged device's serial number"
                    disabled={reveal.isSuccess}
                  />
                </Field>
              )}

              <Field
                label="Reason"
                required
                hint="Recorded in the audit trail — an unexplained reveal is worthless."
              >
                <Textarea
                  value={reason}
                  onChange={(_, d) => setReason(d.value)}
                  placeholder="e.g. reconciling the failed join, ticket 4471"
                  disabled={reveal.isSuccess}
                />
              </Field>

              {errorMessage && (
                <p className="font-display text-[13px]" style={{ color: '#DC2626' }}>
                  {errorMessage}
                </p>
              )}

              {reveal.isSuccess && (
                <div
                  className="flex flex-col gap-2 p-3"
                  style={{ border: `1px solid ${STROKE.base}`, backgroundColor: SURFACE.base }}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-sm" data-testid="revealed-password">
                      {reveal.data.recoveryPassword}
                    </span>
                    <Button
                      size="small"
                      icon={<Copy size={14} strokeWidth={1.5} />}
                      onClick={async () => {
                        await navigator.clipboard.writeText(reveal.data.recoveryPassword);
                        setCopied(true);
                      }}
                    >
                      {copied ? 'Copied' : 'Copy'}
                    </Button>
                  </div>
                  {/* The OWNING job — after a re-add the device has two jobIds, and
                      the operator needs the OLD one until it re-migrates. */}
                  <p className="font-display text-xs" style={{ color: TEXT.secondary }}>
                    Belongs to job <span className="font-mono">{reveal.data.jobId}</span> in
                    campaign <span className="font-mono">{reveal.data.migrationId}</span>
                    {reveal.data.failedActionId
                      ? ` — salvaged at ${reveal.data.failedActionId}`
                      : ''}
                    . If this device was re-added to a newer campaign, this password is still
                    the one that works until the new migration completes.
                  </p>
                </div>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={close}>
              {reveal.isSuccess ? 'Close' : 'Cancel'}
            </Button>
            {!reveal.isSuccess && (
              <Button
                appearance="primary"
                icon={reveal.isPending ? <Spinner size="tiny" /> : <KeyRound size={16} strokeWidth={1.5} />}
                disabled={reveal.isPending || !reason.trim() || !effectiveSerial}
                onClick={() => reveal.mutate()}
              >
                Reveal
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
